/*
 * H1. One chrome for grouped adoption pages.
 *
 * The complaint was the count: twenty nine adoption and CoE pages, fourteen
 * sidebar items for a champion. The instruction was to group rather than
 * delete, the way the company page became three tabs, and to keep every page
 * reachable by address afterwards.
 *
 * So this hosts existing pages as tabs. Nothing is rewritten and nothing is
 * deleted: a hub mounts the same page components the routes always mounted, and
 * the old routes still resolve, so every link in a lesson or an email keeps
 * working. What changes is how many things the sidebar has to say.
 *
 * Two rules make the grouping honest rather than cosmetic:
 *   - A tab the caller has no grant for is not drawn (G3's filter, same
 *     helper). Grouping must never smuggle a surface past a permission.
 *   - A hub with one surviving tab draws no tab bar at all. A single tab is
 *     furniture pretending to be a choice.
 *
 * Each panel keeps its own page heading, like CompanyTabPanel: the hub supplies
 * the tab bar and nothing else, so there is never a title above a title.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';

export interface HubTab {
  /** Slug used in `?tab=`. Stable: links point at it. */
  id: string;
  label: string;
  icon: React.ElementType;
  /** The grant this surface needs. Undefined means everyone who reaches the
   *  hub may open it. */
  perm?: string;
  /** Only for someone enrolled in the adoption program. The plain client shell
   *  predates it and holds no workspace role, so it fails open on permissions
   *  (see usePermissions); a tab that would be empty for them is dropped here
   *  instead of drawn and then apologised for. */
  enrolledOnly?: boolean;
  /** Rendered when the tab is open. A function so a heavy page only mounts
   *  when someone actually opens it. */
  render: () => React.ReactNode;
}

export interface AdoptionHubProps {
  /** Screen-reader name for the tab bar. */
  label: string;
  tabs: HubTab[];
}

const AdoptionHub: React.FC<AdoptionHubProps> = ({ label, tabs }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { can, enrolled } = usePermissions();

  const offered = useMemo(
    () => tabs.filter((t) => can(t.perm) && (!t.enrolledOnly || enrolled)),
    // `can` is rebuilt each render; the permission list behind it is what
    // matters, and it only changes when /aai/me resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tabs, enrolled],
  );

  const [active, setActive] = useState<string>(() => searchParams.get('tab') || '');

  useEffect(() => {
    // The URL is authoritative, INCLUDING when it carries no tab. Only setting
    // this when a tab was present meant a link to the bare hub (a "See it in My
    // Tools" button, say) changed the address and left the previous tab on
    // screen, so the button read as doing nothing.
    setActive(searchParams.get('tab') || '');
  }, [searchParams]);

  const pick = useCallback((id: string) => {
    setActive(id);
    setSearchParams({ tab: id }, { replace: true });
  }, [setSearchParams]);

  if (offered.length === 0) return null;

  // The permission list arrives after the first paint, so the open tab can
  // disappear from under us. Fall back to the first offered tab rather than
  // rendering a hub with nothing in it.
  const shown = offered.find((t) => t.id === active) ?? offered[0];

  return (
    <div className="space-y-5">
      {offered.length > 1 && (
        <div className="border-b border-line">
          <nav className="-mb-px flex items-end gap-1 overflow-x-auto" aria-label={label}>
            {offered.map((t) => {
              const Icon = t.icon;
              const on = shown.id === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => pick(t.id)}
                  className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ${
                    on
                      ? 'border-brand-500 text-brand-600'
                      : 'border-transparent text-ink-dim hover:border-line-strong hover:text-ink'
                  }`}
                >
                  <Icon size={16} />
                  {t.label}
                </button>
              );
            })}
          </nav>
        </div>
      )}
      <div>{shown.render()}</div>
    </div>
  );
};

export default AdoptionHub;
